import { useEffect, useMemo, useRef, useState } from "react";

import { X } from "lucide-react";
import { useDispatch } from "react-redux";

import { useConfirmRecordingConsent } from "@/hooks/mutations/use-transcription";
import { useUserProfile } from "@/hooks/useUserProfile";
import { showError } from "@/utils/notification";

import { Button } from "@components/ui/button";
import { Checkbox } from "@components/ui/checkbox";

import { ConsentInfoTooltip } from "./ConsentInfoTooltip";
import {
  DEFAULT_SPEAKERS,
  SPEAKER_ROLES,
  SpeakerChecklist,
  type SpeakerHint,
  type SpeakerRole,
} from "./SpeakerChecklist";

type AudioRecorderProps = {
  handleClose: () => void;
  examinee_name: string;
  caseNumber: string;
  /**
   * Begin the recording session (MDRS-116). Receives the diarization speaker
   * count derived from the checked roles, the ordered speaker labels — the
   * SAME names shown checked in this modal (e.g. ["Dr. Sarah Johnson",
   * "Madeleine VAN VELZEN", "Interpreter"]), not generic role words — so the
   * live view names speakers by who's actually in the room instead of a
   * generic "Speaker C" (see extendSpeakerRoles's `roleOrder` param), and the
   * same names paired with their role (MDRS-163 follow-on) — forwarded all
   * the way to the batch diarization pipeline once the session ends and gets
   * reprocessed, so real-name speaker identification isn't limited to the
   * live heuristic. The live session then renders in the content panel — this
   * modal is consent-only and closes on start.
   */
  onStartRecording: (
    maxSpeakers: number,
    roleOrder: string[],
    speakerHints: SpeakerHint[]
  ) => void | Promise<void>;
  /** True while the session handshake is in flight (Start Recording disabled). */
  isConnecting?: boolean;
};

/**
 * The Consent Confirmation gate (MDRS-115, kept as a modal in Phase 3). Hosts
 * the consent card, the speaker checklist, and the Go Back / Start Recording
 * footer. On confirm it hands the derived `maxSpeakers` to the owner, which
 * starts the session and moves the LIVE view into the content panel (MDRS-116).
 * The live recording box previously here now lives in `ScribeSession`.
 */
export const AudioRecorder = ({
  handleClose,
  examinee_name,
  caseNumber,
  onStartRecording,
  isConnecting = false,
}: AudioRecorderProps) => {
  const dispatch = useDispatch();
  const confirmRecordingConsent = useConfirmRecordingConsent();
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  // Which speaker roles are present. Cosmetic list (MDRS-115); the count of
  // checked roles derives the diarization speaker count.
  const [speakers, setSpeakers] =
    useState<Record<SpeakerRole, boolean>>(DEFAULT_SPEAKERS);

  // The examiner is the logged-in user, so the Doctor row defaults to their
  // name; the Examinee row defaults to the case's examinee. Both fall back to
  // the role word. These are only the DEFAULTS — the user can retype any of
  // them before starting (e.g. naming the Interpreter/Other role, or fixing a
  // wrong profile name) via SpeakerChecklist's now-editable name field.
  const userProfile = useUserProfile();
  const defaultSpeakerLabels: Record<SpeakerRole, string> = useMemo(
    () => ({
      Doctor: userProfile?.name || "Doctor",
      Examinee: examinee_name || "Examinee",
      Interpreter: "Interpreter",
      Other: "Other",
    }),
    [userProfile?.name, examinee_name]
  );

  // User-editable speaker names, seeded from the defaults above. Only fields
  // the user hasn't touched track the default as it resolves (e.g. the
  // profile/case fetch landing after mount); once edited, a field is frozen
  // against further default changes so a manual edit is never clobbered.
  const [speakerNames, setSpeakerNames] =
    useState<Record<SpeakerRole, string>>(defaultSpeakerLabels);
  const touchedRef = useRef<Set<SpeakerRole>>(new Set());

  useEffect(() => {
    setSpeakerNames((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const role of SPEAKER_ROLES) {
        if (
          !touchedRef.current.has(role) &&
          next[role] !== defaultSpeakerLabels[role]
        ) {
          next[role] = defaultSpeakerLabels[role];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [defaultSpeakerLabels]);

  const handleNameChange = (role: SpeakerRole, value: string) => {
    touchedRef.current.add(role);
    setSpeakerNames((prev) => ({ ...prev, [role]: value }));
  };

  // Checked speakers paired with their role (MDRS-163 follow-on) — the SAME
  // names rendered in the checklist below (user-edited or defaulted
  // doctor/examinee/interpreter/other names), in the checklist's fixed order
  // (Doctor, Examinee, Interpreter, Other). A blank/whitespace-only edit falls
  // back to the role's default. This is the single source of truth: forwarded
  // to the batch diarization pipeline for real-name speaker identification
  // once the session ends, and also the source for `checkedRoleOrder` below
  // (the live heuristic only needs the plain names, not the roles).
  const speakerHints: SpeakerHint[] = useMemo(
    () =>
      SPEAKER_ROLES.filter((role) => speakers[role]).map((role) => {
        const trimmed = speakerNames[role]?.trim();
        return { role, name: trimmed || defaultSpeakerLabels[role] };
      }),
    [speakers, speakerNames, defaultSpeakerLabels]
  );

  // Number of voices Azure should diarize (1 checked → solo dictation; 2+ →
  // real-time diarization) AND the label sequence handed to extendSpeakerRoles
  // so live speakers are named after who's actually in the room instead of a
  // generic "Examiner"/"Speaker C".
  const checkedRoleOrder = useMemo(
    () => speakerHints.map((hint) => hint.name),
    [speakerHints]
  );
  const maxSpeakers = checkedRoleOrder.length;

  // Record consent server-side BEFORE starting the session (SCRIBE-02) — the
  // streaming-token endpoints reject the recording otherwise. Keep the modal
  // open on failure so the user can retry rather than silently proceeding
  // into a session that will fail to obtain a token.
  const handleStart = async () => {
    try {
      await confirmRecordingConsent.mutateAsync(caseNumber);
    } catch (err) {
      showError(
        err instanceof Error
          ? err.message
          : "Failed to confirm recording consent. Please try again.",
        dispatch
      );
      return;
    }
    onStartRecording(maxSpeakers, checkedRoleOrder, speakerHints);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <h2 className="text-lg font-semibold text-background-dark">
            Consent Confirmation
          </h2>
          <ConsentInfoTooltip />
        </div>
        <button
          onClick={handleClose}
          className="text-foreground-muted hover:text-foreground"
          aria-label="Close"
        >
          <X className="size-5" />
        </button>
      </div>

      <div className="space-y-4 rounded-lg border border-primary-100 bg-primary-50 p-4">
        <p className="text-sm text-foreground">
          You can only proceed with recording the assessment if informed consent has
          been obtained from the examinee.
        </p>
        <div className="flex items-start gap-3">
          <Checkbox
            id="consent-confirmed"
            checked={consentConfirmed}
            onCheckedChange={(checked) => setConsentConfirmed(checked === true)}
            disabled={isConnecting || confirmRecordingConsent.isPending}
            className="mt-0.5"
          />
          <label
            htmlFor="consent-confirmed"
            className="cursor-pointer text-sm font-medium leading-tight"
          >
            I confirm that I have obtained the examinee&apos;s informed consent to
            use Assessment Scribe to record and transcribe this assessment.
          </label>
        </div>
      </div>

      <SpeakerChecklist
        speakers={speakers}
        labels={speakerNames}
        sessionLocked={isConnecting || confirmRecordingConsent.isPending}
        onToggle={(role, checked) =>
          setSpeakers((prev) => ({ ...prev, [role]: checked }))
        }
        onNameChange={handleNameChange}
      />

      <div className="flex gap-4 pt-4">
        <Button variant="outline" className="flex-1" onClick={handleClose}>
          Go Back
        </Button>
        <Button
          className="flex-1"
          onClick={handleStart}
          isLoading={isConnecting || confirmRecordingConsent.isPending}
          disabled={
            !consentConfirmed ||
            maxSpeakers < 1 ||
            isConnecting ||
            confirmRecordingConsent.isPending
          }
        >
          Start Recording
        </Button>
      </div>
    </div>
  );
};
